import { Avatar, Table, type TableColumn } from '@dev-spendesk/grapes';
import React from 'react';

import { EllipsisTooltip } from 'common/components/EllipsisTooltip';
import { useTranslation } from 'common/hooks/useTranslation';

import { type MemberStatus, type MemberTableRow } from '../../models/member';

type Props = {
  members: MemberTableRow[];
  onMemberClick?({ id }: { id: string }): void;
  isCompanyManagedByIntegration?: boolean;
};

export const MembersPaginatedTable = ({
  members,
  onMemberClick,
  isCompanyManagedByIntegration,
}: Props) => {
  const { t } = useTranslation('global');
  const onRowClick = React.useCallback(
    (row: MemberTableRow) => !!onMemberClick && onMemberClick({ id: row.id }),
    [onMemberClick],
  );
  const getRowId = React.useCallback((member: MemberTableRow) => member.id, []);

  const memberColumns: TableColumn<MemberTableRow>[] = React.useMemo(() => {
    const columns: TableColumn<MemberTableRow>[] = [
      {
        header: t('members.table.name'),
        id: 'name',
        width: '280px',
        renderCell: (member) => (
          <div className="flex items-center overflow-hidden">
            {!isCompanyManagedByIntegration && (
              <div className="mr-16">
                <Avatar
                  src={member.avatar}
                  text={getMemberName(member)}
                  size={32}
                />
              </div>
            )}
            <div className="grid" data-mask>
              <EllipsisTooltip
                className="overflow-hidden overflow-ellipsis whitespace-nowrap text-content-primary body-m"
                text={getMemberName(member)}
                data-mask
              />
              {showExtraInformation(member.status) && (
                <EllipsisTooltip
                  className="text-content-primary body-s"
                  text={member.email || member.deletedEmail}
                  tooltipMaxWidth={400}
                />
              )}
            </div>
          </div>
        ),
        getSortValue: (member: MemberTableRow) => getMemberName(member),
      },
    ];
    return columns;
  }, []);

  return (
    <Table
      columns={memberColumns}
      data={members}
      getRowId={getRowId}
      getIsRowDisabled={(row) => row.status === 'archived'}
      onRowClick={onRowClick}
      emptyState={{
        title: t('members.table.noResults.title'),
        subtitle: t('members.table.noResults.subtitle'),
      }}
      defaultSortState={{
        columnId: 'name',
        direction: 'ascending',
      }}
      nextSortDirection={{
        none: 'descending',
        ascending: 'descending',
        descending: 'ascending',
      }}
    />
  );
};

const showExtraInformation = (memberStatus: MemberStatus) => {
  return memberStatus !== 'pending';
};

const getMemberName = (member: MemberTableRow) => {
  return member.status === 'pending'
    ? member.email
    : `${member.firstName} ${member.lastName}`;
};
